import { INIT_PAGINATION } from '@vindr/services/constants/common.constant';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import useControlModal from '@vindr/utils/hooks/useControlModal';
import { PaginationProps, SettingsContext } from '@vindr/services/interfaces';
import { useGetAccounts } from '@vindr/services/actions/account.action';
import { filterValidValue } from '@vindr/utils/helpers/common.helper';
import { createContext, useContext, useMemo, useState } from 'react';
import { ImmerHook, useImmer } from 'use-immer';

const AccountContext = createContext({});

export const INIT_FILTER_ACCOUNT: Parameters<typeof useGetAccounts>[0] = {
  sort_field: 'updated_at',
  sort_order: SORT_ORDER.DESC,
};
export interface AccountContext extends SettingsContext {
  accountList: ReturnType<typeof useGetAccounts>['data']['data'];
  refetch: ReturnType<typeof useGetAccounts>['refetch'];
  editItem: AccountContext['accountList'][number];
  filter: Parameters<typeof useGetAccounts>[0];
  setFilter: ImmerHook<AccountContext['filter']>[1];
}

const AccountProvider = ({ children }: { children: JSX.Element }) => {
  const [editItem, setEditItem] = useImmer<any>({});
  const [stateAction, setStateAction] = useState<'create' | 'edit'>('create');
  const [filter, setFilter] = useImmer<Parameters<typeof useGetAccounts>[0]>(INIT_FILTER_ACCOUNT);
  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);

  const { openModal, setCloseModal, setOpenAddModal, setOpenEditModal } = useControlModal(
    setEditItem,
    setStateAction
  );

  const { data, isFetching, refetch } = useGetAccounts({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    ...filterValidValue(filter),
  });

  const { count: total, data: accountList } = data || {};

  const memoizedValue = useMemo(
    () => ({
      accountList,
      editItem,
      filter,
      isLoading: isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      stateAction,
      total,
    }),
    [
      accountList,
      editItem,
      filter,
      isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      stateAction,
      total,
    ]
  );

  return <AccountContext.Provider value={memoizedValue}>{children}</AccountContext.Provider>;
};
AccountProvider.displayName = 'AccountProvider';

export const useAccountContext = () => {
  const context = useContext(AccountContext);

  if (!context) {
    throw new Error('useAccountContext must be used within a AccountProvider');
  }

  return context as AccountContext;
};

export default AccountProvider;
